import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicPage from '../components/public/PublicPage';
import FormField from '../components/common/FormField';
import Alert from '../components/common/Alert';
import usePageTitle from '../hooks/usePageTitle';
import publicService from '../services/publicService';
import { SITE } from '../config/site';
import { validateEmail } from '../utils/validators';
import './PublicPages.css';

// Values mirror ContactMessage::TOPICS on the server.
const TOPICS = [
  { value: 'general', label: 'General question' },
  { value: 'landlord', label: 'Landlords and agents' },
  { value: 'applicant', label: 'Applicants' },
  { value: 'privacy', label: 'My personal information (access, correction, deletion)' },
  { value: 'problem', label: 'Report a problem' },
  { value: 'security', label: 'Report a security issue' },
  { value: 'media', label: 'Media or partnerships' }
];

const EMPTY = { name: '', email: '', topic: '', message: '', accept: false, website: '' };

const ContactPage = () => {
  usePageTitle('Contact', `Contact ${SITE.name}: questions, privacy requests, problems and partnerships.`);

  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const change = (e) => {
    const { name, type, checked, value } = e.target;
    setValues((v) => ({ ...v, [name]: type === 'checkbox' ? checked : value }));
  };

  const validate = () => {
    const errs = {};
    if (!values.name.trim()) errs.name = 'Enter your name.';
    const emailError = validateEmail(values.email);
    if (emailError) errs.email = emailError;
    if (!values.topic) errs.topic = 'Choose what your message is about.';
    if (values.message.trim().length < 10) errs.message = 'Please write at least a sentence (10 characters).';
    if (values.message.length > 3000) errs.message = 'Please keep it under 3000 characters.';
    if (!values.accept) errs.accept = 'Please tick the box so we can reply to you.';
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    setError('');
    try {
      await publicService.sendContactMessage({
        name: values.name.trim(),
        email: values.email.trim(),
        topic: values.topic,
        message: values.message.trim(),
        accept: true,
        website: values.website
      });
      setSent(true);
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors) {
        setErrors(Object.fromEntries(Object.entries(apiErrors).map(([k, v]) => [k, v[0]])));
      } else if (err.response?.status === 429) {
        setError('You have sent a few messages already. Please wait a few minutes and try again.');
      } else {
        setError('Your message could not be sent. Please try again, or email us if the problem continues.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicPage>
      <section className="pub-hero">
        <div className="public-container pub-narrow">
          <p className="pub-eyebrow">Contact</p>
          <h1>How can we help?</h1>
          <p className="pub-lead">
            Send us a message and we will reply by email. For questions about your own information, please choose
            &quot;My personal information&quot;.
          </p>
        </div>
      </section>

      <section className="pub-section">
        <div className="public-container pub-contact">
          <div className="pub-contact__form">
            {sent ? (
              <div className="pub-card" role="status">
                <h2>Thank you</h2>
                <p>We have your message and will reply by email to <strong>{values.email}</strong>.</p>
                <button type="button" className="public-btn public-btn--ghost" onClick={() => { setValues(EMPTY); setSent(false); }}>
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate aria-label="Contact form">
                {error && <Alert type="error" message={error} />}
                <div className="pub-form-row">
                  <FormField label="Your name" name="name" value={values.name} onChange={change} error={errors.name} required autoComplete="name" />
                  <FormField label="Email address" name="email" type="email" value={values.email} onChange={change} error={errors.email} required autoComplete="email" />
                </div>
                <FormField label="What is it about?" name="topic" type="select" options={TOPICS} value={values.topic} onChange={change} error={errors.topic} required />
                <FormField label="Your message" name="message" type="textarea" rows={6} value={values.message} onChange={change} error={errors.message} required maxLength={3000} />

                {/* Honeypot: hidden from people and screen readers. Bots fill it in and are quietly ignored. */}
                <div className="pub-honeypot" aria-hidden="true">
                  <label htmlFor="website">Leave this field empty</label>
                  <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={values.website} onChange={change} />
                </div>

                <label className="pub-check">
                  <input type="checkbox" name="accept" checked={values.accept} onChange={change} aria-invalid={!!errors.accept} />
                  <span>
                    I agree that {SITE.name} may use these details to reply to me, as described in the{' '}
                    <Link to="/privacy" target="_blank">Privacy Policy</Link>.
                  </span>
                </label>
                {errors.accept && <p className="pub-error" role="alert">{errors.accept}</p>}

                <button type="submit" className="public-btn public-btn--primary" disabled={busy}>
                  {busy ? 'Sending...' : 'Send message'}
                </button>
              </form>
            )}
          </div>

          <aside className="pub-contact__info" aria-label="Other ways to reach us">
            {SITE.supportEmail && (
              <div className="pub-card">
                <h2>Email</h2>
                <p><a href={`mailto:${SITE.supportEmail}`}>{SITE.supportEmail}</a></p>
              </div>
            )}
            {SITE.phone && (
              <div className="pub-card">
                <h2>Phone</h2>
                <p><a href={`tel:${SITE.phone.replace(/\s+/g, '')}`}>{SITE.phone}</a></p>
              </div>
            )}
            {SITE.address && (
              <div className="pub-card">
                <h2>Address</h2>
                <p>{SITE.address}</p>
              </div>
            )}
            <div className="pub-card">
              <h2>Your own information</h2>
              <p>
                If you have an account, you can see your consents, download your data and request deletion yourself under{' '}
                <em>My data</em> after you <Link to="/login">log in</Link>.
              </p>
              {SITE.informationOfficerName && <p>Our Information Officer is {SITE.informationOfficerName}.</p>}
            </div>
            <div className="pub-card">
              <h2>Security issues</h2>
              <p>
                If you have found a security problem, choose &quot;Report a security issue&quot;. Please do not include
                other people&apos;s personal information in your message.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </PublicPage>
  );
};

export default ContactPage;
